import { assetPath } from './appPaths';

export const MAX_BOARD_GALLERY_IMAGE_BYTES = 1024 * 1024;
export const MAX_BOARD_GALLERY_IMAGE_MB = 1;
export const MAX_BOARD_GALLERY_IMAGE_DIMENSION = 400;

const MIN_BOARD_GALLERY_DIMENSION = 120;
const QUALITY_STEPS = [0.86, 0.78, 0.7, 0.62, 0.54, 0.46, 0.38];

export const normalizeBoardGalleryImage = (image) => {
  if (typeof image === 'string') {
    const url = image.trim();

    if (!url) {
      return null;
    }

    return {
      url,
      caption: ''
    };
  }

  if (!image || typeof image !== 'object' || Array.isArray(image)) {
    return null;
  }

  const url = String(
    image.url ?? image.src ?? image.image ?? image.path ?? image.reference ?? ''
  ).trim();

  if (!url) {
    return null;
  }

  const hasExplicitCaption = image.caption !== undefined && image.caption !== null;
  const caption = hasExplicitCaption
    ? String(image.caption)
    : String(image.label ?? image.title ?? image.alt ?? '').trim();

  return {
    url,
    caption
  };
};

export const normalizeBoardGalleryImages = (images) => {
  if (Array.isArray(images)) {
    return images
      .map(normalizeBoardGalleryImage)
      .filter(Boolean);
  }

  if (images && typeof images === 'object') {
    const normalizedImage = normalizeBoardGalleryImage(images);
    return normalizedImage ? [normalizedImage] : [];
  }

  if (typeof images === 'string') {
    const trimmedImages = images.trim();

    if (!trimmedImages) {
      return [];
    }

    try {
      const parsedImages = JSON.parse(trimmedImages);
      return normalizeBoardGalleryImages(parsedImages);
    } catch (error) {
      const normalizedImage = normalizeBoardGalleryImage(trimmedImages);
      return normalizedImage ? [normalizedImage] : [];
    }
  }

  return [];
};

export const resolveBoardGalleryImageSrc = (image) => {
  const normalizedImage = normalizeBoardGalleryImage(image);
  const url = normalizedImage?.url || '';

  if (!url) {
    return '';
  }

  if (/^(data:image\/|https?:\/\/)/i.test(url)) {
    return url;
  }

  return assetPath(url);
};

export const getBoardGalleryImageCaptionValue = (image) =>
  normalizeBoardGalleryImage(image)?.caption || '';

export const getBoardGalleryImageCaption = (image) =>
  getBoardGalleryImageCaptionValue(image).trim();

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`${file.name} could not be opened as an image.`));
    };

    image.src = objectUrl;
  });

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('Image compression failed.'));
      },
      'image/jpeg',
      quality
    );
  });

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read the compressed image.'));
    reader.readAsDataURL(blob);
  });

const drawImageToCanvas = (image, width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { alpha: false });

  if (!context) {
    throw new Error('Image compression is not supported in this browser.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  return canvas;
};

export const compressBoardGalleryFile = async (file) => {
  if (!file || !file.type?.startsWith('image/') || file.type === 'image/svg+xml') {
    throw new Error(`${file?.name || 'Selected file'} must be a PNG, JPEG, WebP, or GIF image.`);
  }

  const image = await loadImageFromFile(file);
  const initialScale = Math.min(
    1,
    MAX_BOARD_GALLERY_IMAGE_DIMENSION / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height)
  );

  let width = Math.max(1, Math.round((image.naturalWidth || image.width) * initialScale));
  let height = Math.max(1, Math.round((image.naturalHeight || image.height) * initialScale));

  for (let resizeAttempt = 0; resizeAttempt < 6; resizeAttempt += 1) {
    const canvas = drawImageToCanvas(image, width, height);
    let smallestBlob = null;

    for (const quality of QUALITY_STEPS) {
      const blob = await canvasToBlob(canvas, quality);

      if (!smallestBlob || blob.size < smallestBlob.size) {
        smallestBlob = blob;
      }

      if (blob.size <= MAX_BOARD_GALLERY_IMAGE_BYTES) {
        return {
          url: await blobToDataUrl(blob),
          caption: ''
        };
      }
    }

    if (
      !smallestBlob ||
      width <= MIN_BOARD_GALLERY_DIMENSION ||
      height <= MIN_BOARD_GALLERY_DIMENSION
    ) {
      break;
    }

    width = Math.max(MIN_BOARD_GALLERY_DIMENSION, Math.round(width * 0.85));
    height = Math.max(MIN_BOARD_GALLERY_DIMENSION, Math.round(height * 0.85));
  }

  throw new Error(`${file.name} could not be compressed below ${MAX_BOARD_GALLERY_IMAGE_MB} MB.`);
};

export const compressBoardGalleryFiles = async (files) => {
  const compressedImages = [];

  for (const file of files) {
    // Sequential compression keeps memory usage predictable when multiple images are selected.
    compressedImages.push(await compressBoardGalleryFile(file));
  }

  return compressedImages;
};
